import {
  ArrowLeftRight,
  CheckCircle2,
  CircleDashed,
  Eraser,
  KeyRound,
  ListChecks,
  Loader2,
  MinusCircle,
  RefreshCw,
  Search,
  Send,
  Star,
  Trash2,
  Wallet,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  SelectMenu,
  SelectMenuItem,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import type {
  AccountCheck,
  PaymentSetItem,
  PaymentSetItemStatus,
  StandAccountRow,
  TestPaymentResult,
} from '../testrun-types';
import {
  useCleanup,
  useCleanupPreview,
  useLatestOtp,
  usePaymentSet,
  useSetMainAccount,
  useStartPaymentSet,
  useTestPayment,
  useTopUp,
  type AccountCredentials,
  type OtpResult,
} from './api';
import { isUsable, parseSum, payerOf, useAccountFormat } from './format';
import { Fail, Notice, PanelHead, useConfirm } from './shared';

/**
 * Actions with the account on the stand. All of them are real operations: the
 * OTP goes to the account's phone, the money stays in the company. Every one
 * that moves money or deletes asks first. Only QA and Admin see them.
 */

type Usable = StandAccountRow & { id: string };

const PAYMENT_STATUSES = ['IN_PROGRESS_AT_BANK', 'SIGNED', 'SUCCEED', 'NEW'] as const;

const usePaymentStatus = () => {
  const { t } = useT();
  return (status: string | null): string =>
    status && (PAYMENT_STATUSES as readonly string[]).includes(status)
      ? t(`automation.accounts.payStatus.${status as (typeof PAYMENT_STATUSES)[number]}`)
      : (status ?? t('automation.accounts.payStatus.unknown'));
};

const RefreshButton = ({ onRefresh, label }: { onRefresh: () => void; label: string }) => (
  <Button variant="ghost" size="sm" onClick={onRefresh} className="self-start">
    <RefreshCw className="size-3.5" aria-hidden="true" />
    {label}
  </Button>
);

const PaymentDone = ({
  result,
  children,
  onRefresh,
}: {
  result: TestPaymentResult;
  children?: ReactNode;
  onRefresh?: (() => void) | undefined;
}) => {
  const { t } = useT();
  const { money, seconds } = useAccountFormat();
  const statusText = usePaymentStatus();
  return (
    <Notice
      tone="pass"
      title={t('automation.accounts.act.paymentDone', {
        amount: money(result.amount, result.currency ?? 'KZT'),
        from: result.from.name,
        to: result.to.name,
        status: statusText(result.status),
      })}
    >
      <div className="space-y-1">
        {children}
        <p className="font-mono text-[11px]">
          id {result.id} · {result.steps.map((s) => `${s.name} ${seconds(s.ms)}`).join(' · ')} ·{' '}
          {t('automation.accounts.act.total', { time: seconds(result.ms) })}
        </p>
        {onRefresh ? (
          <RefreshButton
            onRefresh={onRefresh}
            label={t('automation.accounts.act.refreshBalances')}
          />
        ) : null}
      </div>
    </Notice>
  );
};

const AccountSelect = ({
  label,
  value,
  onChange,
  accounts,
  emptyLabel,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  accounts: Usable[];
  emptyLabel: string;
}) => {
  const { accountLabel } = useAccountFormat();
  return (
    <Field label={label}>
      {({ id }) =>
        accounts.length ? (
          <SelectMenu id={id} value={value} onValueChange={onChange}>
            {accounts.map((a) => (
              <SelectMenuItem key={a.id} value={a.id}>
                {accountLabel(a)}
              </SelectMenuItem>
            ))}
          </SelectMenu>
        ) : (
          <Input id={id} value={emptyLabel} disabled readOnly />
        )
      }
    </Field>
  );
};

/* ─── a transfer between own accounts ─── */

/** By default 10 ₸ from the payer account to another tenge account. */
const Transfer = ({
  accounts,
  creds,
  onRefresh,
}: {
  accounts: Usable[];
  creds: AccountCredentials;
  onRefresh?: (() => void) | undefined;
}) => {
  const { t } = useT();
  const { money } = useAccountFormat();
  const confirm = useConfirm();
  const pay = useTestPayment();
  const [fromId, setFromId] = useState(() => (payerOf(accounts) ?? accounts[0])?.id ?? '');
  const [toId, setToId] = useState('');
  const [amount, setAmount] = useState('10');
  const from = accounts.find((a) => a.id === fromId);
  const targets = accounts.filter((a) => a.id !== fromId && a.currency === from?.currency);
  const to = targets.find((a) => a.id === toId) ?? targets[0];
  const sum = parseSum(amount);
  const problem =
    !from || !to
      ? t('automation.accounts.act.transfer.needTwo')
      : sum == null
        ? t('automation.accounts.act.transfer.positive')
        : (from.balance ?? 0) < sum
          ? t('automation.accounts.act.transfer.notEnough', {
              account: from.name,
              amount: money(sum, from.currency ?? 'KZT'),
            })
          : null;

  const run = () => {
    if (problem || !from || !to || sum == null) return;
    confirm.ask({
      title: t('automation.accounts.act.transfer.confirm', {
        amount: money(sum, from.currency ?? 'KZT'),
        from: from.name,
        to: to.name,
      }),
      description: t('automation.accounts.act.realPaymentOtp'),
      confirmLabel: t('automation.accounts.act.transfer.go'),
      onConfirm: () => pay.mutate({ ...creds, fromId: from.id, toId: to.id, amount: sum }),
    });
  };

  return (
    <div className="space-y-3">
      <PanelHead
        icon={ArrowLeftRight}
        title={t('automation.accounts.act.transfer.title')}
        hint={t('automation.accounts.act.transfer.hint')}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <AccountSelect
          label={t('automation.accounts.act.transfer.from')}
          value={fromId}
          onChange={(id) => {
            setFromId(id);
            setToId('');
          }}
          accounts={accounts}
          emptyLabel={t('automation.accounts.act.transfer.noAccounts')}
        />
        <AccountSelect
          label={t('automation.accounts.act.transfer.to')}
          value={to?.id ?? ''}
          onChange={setToId}
          accounts={targets}
          emptyLabel={t('automation.accounts.act.transfer.noSameCurrency')}
        />
      </div>
      <div className="flex items-end gap-2">
        <Field label={t('automation.accounts.act.transfer.amount')} className="min-w-0 flex-1">
          {({ id }) => (
            <Input
              id={id}
              className="font-mono"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
            />
          )}
        </Field>
        <Button onClick={run} disabled={Boolean(problem)} loading={pay.isPending}>
          {pay.isPending ? null : <Send className="size-4" aria-hidden="true" />}
          {pay.isPending
            ? t('automation.accounts.act.waitingOtp')
            : t('automation.accounts.act.transfer.go')}
        </Button>
      </div>
      {problem && accounts.length > 0 ? (
        <p className="text-[12px] text-ink-muted">{problem}</p>
      ) : null}
      {pay.isError ? (
        <Fail title={t('automation.accounts.act.transfer.failed')} error={pay.error} />
      ) : null}
      {pay.data ? <PaymentDone result={pay.data} onRefresh={onRefresh} /> : null}
      {confirm.dialog}
    </div>
  );
};

/* ─── top-up of the payer account ─── */

const TOP_UP_PRESETS = [
  { amount: 6_000_000, label: 'presetIp' },
  { amount: 55_000_000, label: 'presetToo' },
] as const;

/** Up to the target from the richest own account — one transfer per press. */
const TopUp = ({
  accounts,
  type,
  creds,
  onRefresh,
}: {
  accounts: Usable[];
  type: 'ИП' | 'ТОО';
  creds: AccountCredentials;
  onRefresh?: (() => void) | undefined;
}) => {
  const { t } = useT();
  const { money } = useAccountFormat();
  const confirm = useConfirm();
  const op = useTopUp();
  const [target, setTarget] = useState(String(type === 'ТОО' ? 55_000_000 : 6_000_000));
  const goal = parseSum(target);
  const main = payerOf(accounts);
  const donors = main
    ? accounts
        .filter((a) => a.id !== main.id && a.currency === main.currency && (a.balance ?? 0) > 1)
        .sort((a, b) => (b.balance ?? 0) - (a.balance ?? 0))
    : [];
  const need = main && goal != null ? Math.max(0, goal - (main.balance ?? 0)) : 0;
  const donor = donors[0];
  const planned = donor ? Math.floor(Math.min(need, (donor.balance ?? 0) - 1)) : 0;
  const plan = !main
    ? t('automation.accounts.act.topUp.noPayer')
    : goal == null
      ? t('automation.accounts.act.topUp.positive')
      : need <= 0
        ? t('automation.accounts.act.topUp.enough', {
            amount: money(main.balance, main.currency ?? 'KZT'),
          })
        : !donor || planned < 1
          ? t('automation.accounts.act.topUp.noDonor')
          : `${t('automation.accounts.act.topUp.plan', { amount: money(planned), donor: donor.name })}${
              planned < need
                ? t('automation.accounts.act.topUp.planShort', { amount: money(need - planned) })
                : ''
            }`;
  const ready = Boolean(main) && goal != null && need > 0 && Boolean(donor) && planned >= 1;

  const run = () => {
    if (!ready || goal == null) return;
    confirm.ask({
      title: t('automation.accounts.act.topUp.confirm', {
        account: main?.name ?? '',
        amount: money(goal),
      }),
      description: `${plan}. ${t('automation.accounts.act.realTransferOtp')}`,
      confirmLabel: t('automation.accounts.act.topUp.go'),
      onConfirm: () => op.mutate({ ...creds, target: goal }),
    });
  };

  return (
    <div className="space-y-3">
      <PanelHead
        icon={Wallet}
        title={t('automation.accounts.act.topUp.title')}
        hint={t('automation.accounts.act.topUp.hint', {
          account: main?.name ?? t('common.empty'),
          main: main?.main ? t('automation.accounts.act.topUp.isMain') : '',
          balance: money(main?.balance, main?.currency ?? 'KZT'),
        })}
      />
      <div className="flex flex-wrap items-end gap-2">
        <Field
          label={t('automation.accounts.act.topUp.target')}
          className="min-w-0 flex-[1_1_130px]"
        >
          {({ id }) => (
            <Input
              id={id}
              className="font-mono"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              inputMode="decimal"
            />
          )}
        </Field>
        {TOP_UP_PRESETS.map((p) => (
          <Button
            key={p.amount}
            size="sm"
            variant={goal === p.amount ? 'primary' : 'ghost'}
            title={t(`automation.accounts.act.topUp.${p.label}Hint`)}
            aria-pressed={goal === p.amount}
            onClick={() => setTarget(String(p.amount))}
          >
            {t(`automation.accounts.act.topUp.${p.label}`)}
          </Button>
        ))}
        <Button onClick={run} disabled={!ready} loading={op.isPending}>
          {op.isPending ? null : <Wallet className="size-4" aria-hidden="true" />}
          {op.isPending
            ? t('automation.accounts.act.topUp.running')
            : t('automation.accounts.act.topUp.go')}
        </Button>
      </div>
      <p className="text-[12px] text-ink-muted">{plan}</p>
      {op.isError ? (
        <Fail title={t('automation.accounts.act.topUp.failed')} error={op.error} />
      ) : null}
      {op.data ? (
        op.data.skipped ? (
          <Notice tone="info">{op.data.message}</Notice>
        ) : (
          <PaymentDone result={op.data} onRefresh={onRefresh}>
            <p>
              {t('automation.accounts.act.topUp.result', {
                before: money(op.data.before),
                after: money(op.data.after),
                target: money(op.data.target),
              })}
              {op.data.shortfall > 0
                ? ` ${
                    op.data.donorsLeft[0]
                      ? t('automation.accounts.act.topUp.shortNext', {
                          amount: money(op.data.shortfall),
                          donor: op.data.donorsLeft[0].name,
                        })
                      : t('automation.accounts.act.topUp.shortNone', {
                          amount: money(op.data.shortfall),
                        })
                  }`
                : ''}
            </p>
          </PaymentDone>
        )
      ) : null}
      {confirm.dialog}
    </div>
  );
};

/* ─── the main account ─── */

/**
 * A user setting, unset by default. AI needs it — the assistant pays from the
 * main account and ai_grps tests fail without it; payments and other tests do not.
 */
const MainAccount = ({
  accounts,
  creds,
  onRefresh,
}: {
  accounts: StandAccountRow[];
  creds: AccountCredentials;
  onRefresh?: (() => void) | undefined;
}) => {
  const { t } = useT();
  const confirm = useConfirm();
  const op = useSetMainAccount();
  const main = accounts.find((a): a is Usable => a.main && Boolean(a.id)) ?? null;
  const options = accounts.filter(
    (a): a is Usable => isUsable(a) && a.currency === 'KZT' && !a.main,
  );
  const [id, setId] = useState('');
  const chosen = options.find((a) => a.id === id) ?? options[0];
  const current = op.data ? op.data.main : main;

  return (
    <div className="space-y-3">
      <PanelHead
        icon={Star}
        title={t('automation.accounts.act.main.title')}
        hint={t('automation.accounts.act.main.hint', {
          current: current
            ? `«${current.name}»${current.number ? ` ${current.number}` : ''}`
            : t('automation.accounts.act.main.unset'),
        })}
      />
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-[1_1_180px]">
          <AccountSelect
            label={t('automation.accounts.act.main.choose')}
            value={chosen?.id ?? ''}
            onChange={setId}
            accounts={options}
            emptyLabel={t('automation.accounts.act.main.noOther')}
          />
        </div>
        <Button
          size="sm"
          disabled={!chosen}
          loading={op.isPending}
          onClick={() => chosen && op.mutate({ ...creds, accountId: chosen.id })}
        >
          {op.isPending ? null : <Star className="size-3.5" aria-hidden="true" />}
          {t('automation.accounts.act.main.set')}
        </Button>
        {main ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={op.isPending}
            onClick={() =>
              confirm.ask({
                title: t('automation.accounts.act.main.removeConfirm', { account: main.name }),
                description: t('automation.accounts.act.main.removeHint'),
                confirmLabel: t('automation.accounts.act.main.remove'),
                destructive: true,
                onConfirm: () => op.mutate({ ...creds, accountId: main.id, remove: true }),
              })
            }
          >
            {t('automation.accounts.act.main.remove')}
          </Button>
        ) : null}
      </div>
      {op.isError ? <Fail title={t('automation.accounts.act.failed')} error={op.error} /> : null}
      {op.data ? (
        <Notice
          tone="pass"
          title={
            op.data.removed
              ? t('automation.accounts.act.main.removed', { account: op.data.changed.name })
              : t('automation.accounts.act.main.isNow', {
                  account: op.data.main?.name ?? op.data.changed.name,
                })
          }
        >
          {onRefresh ? (
            <RefreshButton
              onRefresh={onRefresh}
              label={t('automation.accounts.act.refreshCheck')}
            />
          ) : null}
        </Notice>
      ) : null}
      {confirm.dialog}
    </div>
  );
};

/* ─── a set of test payments ─── */

const SET_ICON: Record<PaymentSetItemStatus, { Icon: LucideIcon; className: string }> = {
  pending: { Icon: CircleDashed, className: 'text-ink-subtle' },
  running: { Icon: Loader2, className: 'animate-spin text-accent' },
  ok: { Icon: CheckCircle2, className: 'text-pass' },
  fail: { Icon: XCircle, className: 'text-fail' },
  skip: { Icon: MinusCircle, className: 'text-ink-subtle' },
};

const SetItemLine = ({ item }: { item: PaymentSetItem }) => {
  const { t } = useT();
  const statusText = usePaymentStatus();
  if (item.status === 'ok') {
    return (
      <p className="text-[12px] text-ink-muted">
        {statusText(item.paymentStatus)}
        {item.note ? ` · ${item.note}` : ''}
        {item.paymentId ? (
          <span className="font-mono" title={item.paymentId}>
            {' '}
            · id {item.paymentId.slice(0, 8)}…
          </span>
        ) : null}
      </p>
    );
  }
  if (item.status === 'fail') {
    return (
      <p className="break-words text-[12px] text-fail [overflow-wrap:anywhere]">{item.detail}</p>
    );
  }
  if (item.status === 'skip') {
    return (
      <p className="text-[12px] text-ink-muted">
        {t('automation.accounts.act.set.skipped', { reason: item.detail ?? '' })}
      </p>
    );
  }
  return (
    <p className="text-[12px] text-ink-muted">
      {item.status === 'running'
        ? t('automation.accounts.act.set.itemRunning')
        : t('automation.accounts.act.set.itemPending')}
    </p>
  );
};

/** One payment of each kind, in the background, with progress. */
const PaymentSet = ({
  creds,
  onRefresh,
}: {
  creds: AccountCredentials;
  onRefresh?: (() => void) | undefined;
}) => {
  const { t } = useT();
  const { money, seconds } = useAccountFormat();
  const confirm = useConfirm();
  const [jobId, setJobId] = useState<string | null>(null);
  const start = useStartPaymentSet();
  const poll = usePaymentSet(jobId);
  const job = poll.data ?? start.data;
  const running = start.isPending || (Boolean(job) && !job?.finishedAt);
  const n = (s: PaymentSetItemStatus) => job?.items.filter((i) => i.status === s).length ?? 0;

  const run = () =>
    confirm.ask({
      title: t('automation.accounts.act.set.confirm'),
      description: t('automation.accounts.act.set.confirmHint'),
      confirmLabel: t('automation.accounts.act.set.start'),
      onConfirm: () => start.mutate(creds, { onSuccess: (j) => setJobId(j.id) }),
    });

  return (
    <div className="space-y-3">
      <PanelHead
        icon={ListChecks}
        title={t('automation.accounts.act.set.title')}
        hint={t('automation.accounts.act.set.hint')}
        action={
          <Button size="sm" onClick={run} disabled={running} loading={running}>
            {running ? null : <ListChecks className="size-3.5" aria-hidden="true" />}
            {running
              ? t('automation.accounts.act.set.running')
              : job
                ? t('automation.accounts.act.set.again')
                : t('automation.accounts.act.set.start')}
          </Button>
        }
      />
      {start.isError ? (
        <Fail title={t('automation.accounts.act.set.notStarted')} error={start.error} />
      ) : null}
      {poll.isError ? (
        <Fail title={t('automation.accounts.act.set.lost')} error={poll.error} />
      ) : null}
      {job ? (
        <div className="space-y-2">
          {job.error ? (
            <Notice tone="fail" title={t('automation.accounts.act.set.noPayments')}>
              {job.error}
            </Notice>
          ) : null}
          <ul className="space-y-2">
            {job.items.map((item) => {
              const { Icon, className } = SET_ICON[item.status];
              return (
                <li key={item.key} className="flex items-start gap-2">
                  <Icon className={cn('mt-0.5 size-4 shrink-0', className)} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-ink">
                      <span className="sr-only">
                        {t(`automation.accounts.act.set.status.${item.status}`)}:{' '}
                      </span>
                      {item.title}{' '}
                      <span className="font-mono text-[11px] text-ink-subtle">
                        {money(item.amount)}
                      </span>
                    </p>
                    <SetItemLine item={item} />
                  </div>
                  <span className="whitespace-nowrap font-mono text-[11px] text-ink-subtle">
                    {item.ms != null ? seconds(item.ms) : ''}
                  </span>
                </li>
              );
            })}
          </ul>
          {job.finishedAt ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[13px] text-ink-muted">
                {t('automation.accounts.act.set.summary', { ok: n('ok'), total: job.items.length })}
                {n('fail')
                  ? `, ${t('automation.accounts.act.set.failedCount', { count: n('fail') })}`
                  : ''}
                {n('skip')
                  ? `, ${t('automation.accounts.act.set.skippedCount', { count: n('skip') })}`
                  : ''}
                {' · '}
                {seconds(Date.parse(job.finishedAt) - Date.parse(job.startedAt))}
              </p>
              {onRefresh ? (
                <RefreshButton
                  onRefresh={onRefresh}
                  label={t('automation.accounts.act.refreshBalances')}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {confirm.dialog}
    </div>
  );
};

/* ─── cleanup ─── */

/** Show the unsigned payments and drafts first, then remove exactly what was shown. */
const Cleanup = ({ creds }: { creds: AccountCredentials }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const { money } = useAccountFormat();
  const confirm = useConfirm();
  const preview = useCleanupPreview();
  const run = useCleanup();
  const list = preview.data;
  // younger than two minutes — maybe an autotest is signing it right now; never touched
  const payIds = list?.payments.filter((p) => !p.fresh).map((p) => p.id) ?? [];
  const draftIds = list?.drafts.map((d) => d.id) ?? [];
  const what = [
    payIds.length ? t('automation.accounts.act.cleanup.cancelN', { count: payIds.length }) : null,
    draftIds.length
      ? t('automation.accounts.act.cleanup.deleteN', { count: draftIds.length })
      : null,
  ]
    .filter(Boolean)
    .join(t('common.andJoin'));

  const go = () => {
    if (!payIds.length && !draftIds.length) return;
    confirm.ask({
      title: t('automation.accounts.act.cleanup.confirm', { what }),
      description: t('automation.accounts.act.cleanup.confirmHint'),
      confirmLabel: t('automation.accounts.act.cleanup.go'),
      destructive: true,
      onConfirm: () =>
        run.mutate(
          { ...creds, paymentIds: payIds, draftIds },
          { onSuccess: () => preview.reset() },
        ),
    });
  };

  return (
    <div className="space-y-3">
      <PanelHead
        icon={Eraser}
        title={t('automation.accounts.act.cleanup.title')}
        hint={t('automation.accounts.act.cleanup.hint')}
        action={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              run.reset();
              preview.mutate(creds);
            }}
            disabled={run.isPending}
            loading={preview.isPending}
          >
            {preview.isPending ? null : <Search className="size-3.5" aria-hidden="true" />}
            {list
              ? t('automation.accounts.act.cleanup.refresh')
              : t('automation.accounts.act.cleanup.find')}
          </Button>
        }
      />
      {preview.isError ? (
        <Fail title={t('automation.accounts.act.cleanup.listFailed')} error={preview.error} />
      ) : null}
      {list ? (
        <div className="space-y-2">
          {!list.payments.length && !list.drafts.length ? (
            <p className="text-[13px] text-ink-muted">
              {t('automation.accounts.act.cleanup.clean')}
            </p>
          ) : null}
          {list.payments.length > 0 ? (
            <div className="space-y-1">
              <p className="text-[13px] font-medium text-ink">
                {t('automation.accounts.act.cleanup.unsigned', { count: list.payments.length })}
              </p>
              {list.payments.map((p) => (
                <p key={p.id} className="text-[12px] text-ink-muted">
                  {p.type ?? t('automation.accounts.act.cleanup.payment')}{' '}
                  {money(p.amount, p.currency)} · {p.from} → {p.to} ·{' '}
                  {p.createdAt ? relative(p.createdAt) : t('common.empty')}
                  {p.status !== 'NEW' ? ` · ${p.status}` : ''}
                  {p.fresh ? (
                    <span className="text-block">
                      {' '}
                      · {t('automation.accounts.act.cleanup.fresh')}
                    </span>
                  ) : null}
                </p>
              ))}
            </div>
          ) : null}
          {list.drafts.length > 0 ? (
            <div className="space-y-1">
              <p className="text-[13px] font-medium text-ink">
                {t('automation.accounts.act.cleanup.drafts', { count: list.drafts.length })}
              </p>
              {list.drafts.map((d) => (
                <p key={d.id} className="text-[12px] text-ink-muted">
                  {d.type ?? t('automation.accounts.act.cleanup.draft')}{' '}
                  {d.amount != null ? money(d.amount, d.currency) : ''} · {d.to}
                  {d.createdAt ? ` · ${relative(d.createdAt)}` : ''}
                </p>
              ))}
            </div>
          ) : null}
          {payIds.length > 0 || draftIds.length > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-fail"
              onClick={go}
              loading={run.isPending}
            >
              {run.isPending ? null : <Trash2 className="size-3.5" aria-hidden="true" />}
              {what.charAt(0).toUpperCase() + what.slice(1)}
            </Button>
          ) : null}
        </div>
      ) : null}
      {run.isError ? (
        <Fail title={t('automation.accounts.act.cleanup.failed')} error={run.error} />
      ) : null}
      {run.data ? (
        <Notice
          tone={run.data.failed.length ? 'warn' : 'pass'}
          title={[
            t('automation.accounts.act.cleanup.result', {
              cancelled: run.data.cancelled.length,
              deleted: run.data.deleted.length,
            }),
            run.data.skipped.length
              ? t('automation.accounts.act.cleanup.resultSkipped', {
                  count: run.data.skipped.length,
                })
              : null,
            run.data.failed.length
              ? t('automation.accounts.act.cleanup.resultFailed', { count: run.data.failed.length })
              : null,
          ]
            .filter(Boolean)
            .join(', ')}
        >
          {run.data.failed.length || run.data.skipped.length ? (
            <ul className="space-y-0.5 font-mono text-[11px]">
              {run.data.failed.map((f) => (
                <li key={`f-${f.id}`}>
                  {f.kind === 'draft'
                    ? t('automation.accounts.act.cleanup.draft')
                    : t('automation.accounts.act.cleanup.payment')}{' '}
                  {f.id}: {f.message}
                </li>
              ))}
              {run.data.skipped.map((s) => (
                <li key={`s-${s.id}`}>
                  {s.id}: {s.reason}
                </li>
              ))}
            </ul>
          ) : null}
        </Notice>
      ) : null}
      {confirm.dialog}
    </div>
  );
};

/* ─── the OTP for a manual login ─── */

export const OtpCode = ({ result }: { result: OtpResult }) => {
  const { t } = useT();
  const { relative } = useFormat();
  if (!result.code) {
    return <span className="text-[13px] text-ink-muted">{t('automation.accounts.otp.none')}</span>;
  }
  return (
    <span className="flex items-baseline gap-2">
      <span
        className="font-mono text-[26px] font-bold tracking-[0.2em] text-ink"
        aria-label={t('automation.accounts.otp.code')}
      >
        {result.code}
      </span>
      <span className="text-[12px] text-ink-subtle">{result.at ? relative(result.at) : ''}</span>
    </span>
  );
};

/** The last OTP sent to the account's phone — for a manual login to the app. */
const OtpPeek = ({ phone }: { phone: string }) => {
  const { t } = useT();
  const otp = useLatestOtp();
  return (
    <div className="space-y-3">
      <PanelHead
        icon={KeyRound}
        title={t('automation.accounts.act.otp.title')}
        hint={t('automation.accounts.act.otp.hint', { phone })}
        action={
          <div className="flex items-center gap-3">
            {otp.data ? <OtpCode result={otp.data} /> : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => otp.mutate(phone)}
              loading={otp.isPending}
            >
              {otp.isPending ? null : <KeyRound className="size-3.5" aria-hidden="true" />}
              {otp.data
                ? t('automation.accounts.act.otp.refresh')
                : t('automation.accounts.act.otp.show')}
            </Button>
          </div>
        }
      />
      {otp.isError ? <Fail title={t('automation.accounts.otp.failed')} error={otp.error} /> : null}
    </div>
  );
};

/* ─── the panel ─── */

const Panel = ({ children }: { children: ReactNode }) => (
  <div className="min-w-0 rounded-lg border border-border bg-canvas p-4">{children}</div>
);

export const StandActions = ({
  data,
  creds,
  onRefresh,
}: {
  data: AccountCheck;
  creds: AccountCredentials;
  onRefresh?: (() => void) | undefined;
}) => {
  const { t } = useT();
  const accounts = (data.accounts ?? []).filter(isUsable);
  return (
    <Card>
      <CardHeader
        title={t('automation.accounts.act.title')}
        description={t('automation.accounts.act.subtitle')}
      />
      {/* two panels a row; one under another on a narrow screen */}
      <div className="grid gap-3 px-4 py-3 lg:grid-cols-2">
        <Panel>
          <Transfer accounts={accounts} creds={creds} onRefresh={onRefresh} />
        </Panel>
        <Panel>
          <TopUp accounts={accounts} type={data.account.type} creds={creds} onRefresh={onRefresh} />
        </Panel>
        <Panel>
          <MainAccount accounts={data.accounts ?? []} creds={creds} onRefresh={onRefresh} />
        </Panel>
        <Panel>
          <PaymentSet creds={creds} onRefresh={onRefresh} />
        </Panel>
        <Panel>
          <Cleanup creds={creds} />
        </Panel>
        <Panel>
          <OtpPeek phone={creds.phone} />
        </Panel>
      </div>
    </Card>
  );
};
